import { motion } from "framer-motion";
import {
  ScanLine, Users, FolderKanban, CalendarDays, BellRing, Target, Bell, Smartphone,
} from "lucide-react";

const FEATURES = [
  { icon: ScanLine, title: "Smart Card Scanning", desc: "Point your camera at any visiting card and TOTO captures every detail in seconds.", span: "lg:col-span-2", tint: "bg-toto-lavender text-toto-blue" },
  { icon: Users, title: "Contact Management", desc: "Every connection organized, searchable and always up to date.", span: "", tint: "bg-toto-violet-soft text-toto-purple" },
  { icon: FolderKanban, title: "Business Categories", desc: "Group contacts by industry, company or your own custom categories.", span: "", tint: "bg-toto-lavender text-toto-blue" },
  { icon: CalendarDays, title: "Networking Events", desc: "Create events and link the people you meet to where you met them.", span: "", tint: "bg-toto-violet-soft text-toto-purple" },
  { icon: BellRing, title: "Follow-Up Reminders", desc: "Never let a promising connection go cold — TOTO nudges you at the right time.", span: "", tint: "bg-toto-lavender text-toto-blue" },
  { icon: Target, title: "Networking Goals", desc: "Set daily goals, build streaks and watch your network grow week by week.", span: "lg:col-span-2", tint: "bg-toto-violet-soft text-toto-purple" },
  { icon: Bell, title: "Notifications", desc: "Progress updates, reminders and milestones — all in one calm feed.", span: "", tint: "bg-toto-lavender text-toto-blue" },
  { icon: Smartphone, title: "Save Contacts to Phone", desc: "One tap saves any scanned card straight into your phone's contacts.", span: "", tint: "bg-toto-violet-soft text-toto-purple" },
];

export const Features = () => (
  <section id="features" className="py-16 sm:py-24" data-testid="features-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl">
        <span className="text-xs font-semibold uppercase tracking-widest text-toto-blue" data-testid="features-eyebrow">Features</span>
        <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-testid="features-heading">
          Everything your network needs, in one app
        </h2>
        <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
          From the first scan to the follow-up call, TOTO keeps every professional connection moving forward.
        </p>
      </div>
      <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        {FEATURES.map(({ icon: Icon, title, desc, span, tint }, i) => (
          <motion.div
            key={title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.45, delay: (i % 4) * 0.07 }}
            className={`group rounded-3xl border border-slate-100 bg-white p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(27,44,200,0.08)] ${span}`}
            data-testid={`feature-card-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          >
            <span className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${tint}`}>
              <Icon className="h-6 w-6" />
            </span>
            <h3 className="mt-5 font-heading text-lg font-semibold text-slate-900">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
